import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { api, ApiError } from './lib/api';
import type { Me } from './lib/types';
import { tg, haptic, isInTelegram } from './lib/telegram';
import { Header } from './components/Header';
import { TabBar, type Tab } from './components/TabBar';
import { Toast } from './components/Toast';
import { Onboarding } from './screens/Onboarding';
import { WheelScreen } from './screens/WheelScreen';
import { TasksScreen } from './screens/TasksScreen';
import { CourseScreen } from './screens/CourseScreen';
import { ShopScreen } from './screens/ShopScreen';
import { ProfileScreen } from './screens/ProfileScreen';
import { PrizesScreen } from './screens/PrizesScreen';

interface Ctx {
  me: Me;
  refresh: () => Promise<void>;
  patch: (b: Partial<Me>) => void;
  toast: (text: string, tone?: 'ok' | 'bad') => void;
  goPrizes: () => void;
  goTab: (t: Tab) => void;
}

const AppCtx = createContext<Ctx | null>(null);
export const useApp = () => useContext(AppCtx)!;

const TITLES: Record<Tab, string> = {
  wheel: 'Крути и выигрывай',
  tasks: 'Задания',
  course: 'Мини-курс',
  shop: 'Магазин',
  profile: 'Профиль',
};

export default function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('wheel');
  const [prizesOpen, setPrizesOpen] = useState(false);
  const [toast, setToastState] = useState<{ text: string; tone: 'ok' | 'bad' } | null>(null);

  const refresh = useCallback(async () => {
    setMe(await api.get<Me>('/me'));
  }, []);

  const showToast = useCallback((text: string, tone: 'ok' | 'bad' = 'ok') => {
    setToastState({ text, tone });
  }, []);

  useEffect(() => {
    api.get<Me>('/me')
      .then(setMe)
      .catch((e: ApiError) => setError(
        e.status === 401 && !isInTelegram
          ? 'Приложение открывается из Telegram — нажми кнопку в боте.'
          : e.message,
      ));
  }, []);

  // Отметка входа: один раз за сессию, сразу после загрузки профиля.
  useEffect(() => {
    if (!me?.terms_accepted) return;
    const today = new Date().toISOString().slice(0, 10);
    if (me.streak_last_date?.slice(0, 10) === today) return;
    api.post<{ alreadyToday: boolean; streakDays: number; weekClosed: boolean; reward: { coins: number; spins: number } }>(
      '/me/daily-checkin',
    ).then((r) => {
      if (r.alreadyToday) return;
      haptic.ok();
      showToast(r.weekClosed
        ? `Неделя закрыта: +${r.reward.coins} коинов и ${r.reward.spins} прокрутов`
        : `День ${r.streakDays}: +${r.reward.coins} коинов, +${r.reward.spins} прокрута`);
      void refresh();
    }).catch(() => {});
  }, [me?.terms_accepted, me?.streak_last_date, refresh, showToast]);

  // Аппаратная кнопка «назад» закрывает призы, а не приложение.
  useEffect(() => {
    const back = () => setPrizesOpen(false);
    if (prizesOpen) { tg.BackButton.show(); tg.BackButton.onClick(back); }
    else tg.BackButton.hide();
    return () => tg.BackButton.offClick(back);
  }, [prizesOpen]);

  if (error) {
    return (
      <Center>
        <p className="mb-2 font-display text-lg">Не открывается</p>
        <p className="max-w-xs text-sm text-ash">{error}</p>
      </Center>
    );
  }
  if (!me) return <Center><Spinner /></Center>;

  if (!me.terms_accepted) {
    return <Onboarding onDone={refresh} />;
  }

  const ctx: Ctx = {
    me, refresh, toast: showToast,
    patch: (b) => setMe((prev) => (prev ? { ...prev, ...b } : prev)),
    goPrizes: () => setPrizesOpen(true),
    goTab: (t) => { setPrizesOpen(false); setTab(t); },
  };

  return (
    <AppCtx.Provider value={ctx}>
      <div className="relative z-10 mx-auto min-h-full max-w-md px-4 pb-24">
        <Header
          title={prizesOpen ? 'Призы' : TITLES[tab]}
          coins={me.coins}
          crystals={me.crystals}
          onCoins={() => setPrizesOpen(true)}
          onCrystals={() => { setPrizesOpen(false); setTab('shop'); }}
        />

        {prizesOpen ? <PrizesScreen onClose={() => setPrizesOpen(false)} /> : (
          <>
            {tab === 'wheel' && <WheelScreen />}
            {tab === 'tasks' && <TasksScreen />}
            {tab === 'course' && <CourseScreen />}
            {tab === 'shop' && <ShopScreen />}
            {tab === 'profile' && <ProfileScreen />}
          </>
        )}
      </div>

      <TabBar tab={tab} onChange={(t) => { setPrizesOpen(false); setTab(t); }} />
      {toast && <Toast text={toast.text} tone={toast.tone} onDone={() => setToastState(null)} />}
    </AppCtx.Provider>
  );
}

function Center({ children }: { children: ReactNode }) {
  return (
    <div className="relative z-10 flex min-h-full flex-col items-center justify-center px-8 text-center">
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-line border-t-gold"
         role="status" aria-label="Загрузка" />
  );
}
